import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import {
  ExpectedStays,
  StayAttention,
  StayCreateInput,
  StayCreateResult,
  StayLinkIssue,
  StayPatchInput,
  StayReservation,
  StayReservationList,
  StaySignInInput,
  StaysBuilding,
  StaysBuildingUnits,
  StaysEntitlement,
  StaysErrorCode,
  StaysHost,
  StaysListing,
  StaysListingAction,
  StaysListingPage,
  StaysListingStatus,
  StaysPatchInput,
  StaysPromoteInput,
  StaysSwitchResult,
  StaysUnitState,
} from '../interfaces/stays';

/**
 * LOBBERO STAYS — the staff client (ai-plans/stays, plan S1; routes 1–11 of
 * lobbero_be/src/stays/routes/).
 *
 * The DEFAULT axios instance on purpose, so every call inherits axios-setup.ts:
 * withCredentials for the session cookies, the X-CSRF-Token double-submit
 * header, and the single-flight 401 → refresh → retry.
 *
 * Every failure is re-thrown as a StaysApiError carrying the server's code,
 * its words (the server `message` IS the product copy for Stays codes), the
 * field map of a 400 and any extra body (the current listing on a 409
 * STAYS_LISTING_CHANGED, the live count on STAYS_BUILDING_HAS_ACTIVE).
 */

export class StaysApiError extends Error {
  status: number;
  code: StaysErrorCode | null;
  field: string | null;
  fields: Record<string, string>;
  extra: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown> | null, fallback: string) {
    const msg = body && typeof body.message === 'string' ? body.message : fallback;
    super(msg);
    this.name = 'StaysApiError';
    this.status = status;
    this.code = body && typeof body.code === 'string' ? (body.code as StaysErrorCode) : null;
    this.field = body && typeof body.field === 'string' && body.field ? body.field : null;
    this.fields = body && body.fields && typeof body.fields === 'object' ? (body.fields as Record<string, string>) : {};
    const rest: Record<string, unknown> = { ...(body || {}) };
    for (const k of ['message', 'code', 'field', 'fields']) delete rest[k];
    this.extra = rest;
  }
}

const wrap = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) {
      throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    }
    throw new StaysApiError(0, null, fallback);
  }
};

export const getStaysEntitlement = (): Promise<StaysEntitlement> =>
  wrap(axios.get<StaysEntitlement>(`${apiRoute}/stays/entitlement`), 'Could not check Lobbero Stays.');

export const listStaysBuildings = (): Promise<{ items: StaysBuilding[] }> =>
  wrap(axios.get(`${apiRoute}/stays/buildings`), 'Could not load your buildings.');

export const setBuildingStays = (buildingId: number, enabled: boolean): Promise<StaysSwitchResult> =>
  wrap(axios.put(`${apiRoute}/stays/buildings/${buildingId}/settings`, { enabled }), 'Could not save the Stays setting.');

export const listBuildingStaysUnits = (buildingId: number): Promise<StaysBuildingUnits> =>
  wrap(axios.get(`${apiRoute}/stays/buildings/${buildingId}/units`), 'Could not load the units.');

export const listStaysHosts = (buildingId: number): Promise<{ items: StaysHost[] }> =>
  wrap(axios.get(`${apiRoute}/stays/buildings/${buildingId}/hosts`), 'Could not load the hosts.');

export const getUnitStays = (unitId: number): Promise<StaysUnitState> =>
  wrap(axios.get(`${apiRoute}/stays/units/${unitId}`), 'Could not load this unit\'s Stays state.');

export const listStaysListings = (
  opts: { buildingId?: number | null; status?: 'all' | StaysListingStatus; cursor?: string | null; limit?: number } = {}
): Promise<StaysListingPage> =>
  wrap(
    axios.get(`${apiRoute}/stays/listings`, {
      params: {
        ...(opts.buildingId ? { building_id: opts.buildingId } : {}),
        status: opts.status || 'all',
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
        ...(opts.limit ? { limit: opts.limit } : {}),
      },
    }),
    'Could not load your Stays listings.'
  );

export const getStaysListing = (id: string): Promise<StaysListing> =>
  wrap(axios.get(`${apiRoute}/stays/listings/${encodeURIComponent(id)}`), 'Could not load this listing.');

export const promoteUnit = (input: StaysPromoteInput): Promise<StaysListing> =>
  wrap(axios.post(`${apiRoute}/stays/listings`, input), 'Could not promote this unit.');

export const updateStaysListing = (id: string, patch: StaysPatchInput): Promise<StaysListing> =>
  wrap(axios.patch(`${apiRoute}/stays/listings/${encodeURIComponent(id)}`, patch), 'Could not save the listing.');

export const actOnStaysListing = (id: string, action: StaysListingAction, version: number): Promise<StaysListing> =>
  wrap(
    axios.post(`${apiRoute}/stays/listings/${encodeURIComponent(id)}/actions`, { action, version }),
    'Could not change the listing.'
  );

// ── Plan S3: stays, their check-in links, the desk lane ────────────────────
const stayPath = (id: string) => `${apiRoute}/stays/reservations/${encodeURIComponent(id)}`;
const listParams = (opts: { state?: 'upcoming' | 'past' | 'all'; from?: string; to?: string }) => ({
  state: opts.state || 'upcoming',
  ...(opts.from ? { from: opts.from } : {}),
  ...(opts.to ? { to: opts.to } : {}),
});

export const listBuildingReservations = (
  buildingId: number,
  opts: { state?: 'upcoming' | 'past' | 'all'; from?: string; to?: string } = {}
): Promise<StayReservationList> =>
  wrap(axios.get(`${apiRoute}/stays/buildings/${buildingId}/reservations`, { params: listParams(opts) }), 'Could not load the stays.');

export const listListingReservations = (
  listingId: string,
  opts: { state?: 'upcoming' | 'past' | 'all' } = {}
): Promise<StayReservationList> =>
  wrap(
    axios.get(`${apiRoute}/stays/listings/${encodeURIComponent(listingId)}/reservations`, { params: listParams(opts) }),
    'Could not load the stays.'
  );

export const getReservation = (id: string): Promise<StayReservation> => wrap(axios.get(stayPath(id)), 'Could not load this stay.');

export const createReservation = (listingId: string, input: StayCreateInput): Promise<StayCreateResult> =>
  wrap(axios.post(`${apiRoute}/stays/listings/${encodeURIComponent(listingId)}/reservations`, input), 'Could not save the stay.');

export const updateReservation = (id: string, patch: StayPatchInput): Promise<StayReservation> =>
  wrap(axios.patch(stayPath(id), patch), 'Could not save the stay.');

export const cancelReservation = (id: string, reason?: string): Promise<StayReservation> =>
  wrap(axios.post(`${stayPath(id)}/cancel`, reason ? { reason } : {}), 'Could not cancel the stay.');

export const rotateLink = (
  id: string,
  opts: { send_email?: boolean } = {}
): Promise<{ link: StayLinkIssue; email: 'sent' | 'spoofed' | 'failed' | 'skipped' }> =>
  wrap(axios.post(`${stayPath(id)}/link`, opts), 'Could not make a new link.');

export const revokeLink = (id: string): Promise<void> =>
  wrap(axios.delete(`${stayPath(id)}/link`), 'Could not stop the link.').then(() => undefined);

export const markLinkSent = (id: string, via: 'copy' | 'whatsapp'): Promise<void> =>
  wrap(axios.post(`${stayPath(id)}/link/sent`, { via }), 'Could not record how the link was sent.').then(() => undefined);

export const getStaysAttention = (buildingId?: number | null): Promise<StayAttention> =>
  wrap(axios.get(`${apiRoute}/stays/attention`, { params: buildingId ? { building_id: buildingId } : {} }), 'Could not load the stays.');

export const listExpectedStays = (buildingId: number): Promise<ExpectedStays> =>
  wrap(axios.get(`${apiRoute}/stays/desk/buildings/${buildingId}/expected`), 'Could not load the short-stay guests.');

/** A refusal of the desk's one tap (the body the tablet acts on rides in `extra`). */
export class SignInFromStayError extends StaysApiError {}

export const signInFromStay = async (reservationId: string, input: StaySignInInput): Promise<Record<string, unknown>> => {
  try {
    return await wrap(
      axios.post(`${apiRoute}/stays/desk/reservations/${encodeURIComponent(reservationId)}/sign-in`, input),
      'Could not sign the guest in.'
    );
  } catch (e) {
    if (e instanceof StaysApiError) {
      throw new SignInFromStayError(e.status, { message: e.message, code: e.code, field: e.field, fields: e.fields, ...e.extra }, e.message);
    }
    throw e;
  }
};
